import { type CursusVak, type Periode } from './types/vakkenoverzicht';

export const appLocale: Intl.LocalesArgument = 'nl-NL';
export const appTimezone = 'Europe/Amsterdam';

// checks whether a periode is niet afgelopen
export const afgelopen = (periode: Periode) => {
  const [lastDay = '1900-01-01'] = periode.dagen.slice(-1);
  const diffInDays = (new Date().getTime() - new Date(lastDay).getTime()) / (1000 * 60 * 60 * 24);

  return diffInDays >= 1;
};

export const capitalize = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

/**
 * Formatting used for a single date.
 */
export const formatDate = (date: string, {
  weekday = true,
  month = false,
  year = false,
} = {}) =>
  new Date(date).toLocaleDateString(appLocale, {
    timeZone: appTimezone,
    weekday: weekday ? 'short' : undefined,
    day: 'numeric',
    month: month ? 'short' : undefined,
    year: year ? 'numeric' : undefined,
  });

/**
 * Formatting for a group of dates, which should be in the same month.
 */
const formatDatesInMonth = (array: Array<string>) => {
  // Split last day and non-last days
  const lastDay = array.pop();
  const nonLastDays = array;
  if (lastDay === undefined) {
    console.error('Unsupported: empty array passed to formatDatesInMonth.');
    return '';
  }

  return nonLastDays.length > 0
    ? `${nonLastDays.map((date) => formatDate(date)).join(', ')} en ${formatDate(lastDay, { month: true })}`
    : formatDate(lastDay, { month: true });
};

/**
 * Formatting for an array of dates, possibly non-sequential.
 * For simplicity we do assume all dates to be within the same year.
 */
export const formatDateArray = (array: Array<string>) => {
  const lastDay = array.slice(-1)[0];
  if (lastDay === undefined) {
    console.error('Unsupported: empty array passed to formatDateArray.');
    return '';
  }

  // Group days per month
  const getMonth = (s: string) => s.slice(5, 7);
  const daysPerMonth = array.reduce<Record<string, Array<string>>>((res, date) => {
    const existingDates = res[getMonth(date)] ?? [];
    existingDates.push(date);
    res[getMonth(date)] = existingDates;
    return res;
  }, {});

  const formattedDays = Object.values(daysPerMonth).map((dates) => formatDatesInMonth(dates));

  return `${formattedDays.join(' en ')} ${new Date(lastDay).getFullYear().toFixed(0)}`;
};

/**
 * Formatting for an array of sequential dates.
 * For simplicity we do assume all dates to be within the same year.
 */
export const formatDateRange = (array: Array<string>) => {
  const firstDay = array[0];
  const lastDay = array.slice(-1)[0];
  if (firstDay === undefined || lastDay === undefined) {
    console.error('Unsupported: empty array passed to formatDateRange.');
    return '';
  }

  switch (array.length) {
    case 1:
      return formatDate(firstDay, { month: true, year: true });
    case 2:
      return `${formatDate(firstDay, { month: true })} en ${formatDate(lastDay, { month: true, year: true })}`;
    default:
      return `${formatDate(firstDay, { month: true })} t/m ${formatDate(lastDay, { month: true, year: true })}`;
  }
};

export const getCursus = (periode: Periode, vak: CursusVak) => vak.periodes[periode.Periode_ID];

export const sleep = async (ms: number): Promise<void> => new Promise((resolve) => {
  setTimeout(resolve, ms);
});
